import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import {
  Plus, Minus, ArrowRight, ArrowLeft, PackageCheck, Search, ShieldAlert, ShieldCheck,
  Ban, CheckCircle2, Loader2, X, Camera, Truck, ClipboardCheck, Lock,
  Sparkles, Wand2, HelpCircle, ChevronDown,
} from "lucide-react";
import { PageHead, Wrap } from "@/components/Content";
import { SquarePayment } from "@/components/SquarePayment";
import { estimateTotal } from "@/data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// The three simple intents a customer chooses from. The customer never has to
// diagnose their own card — that is our job.
const INTENTS = [
  { id: "clean_polish", title: "CLEAN MY CARD", copy: "Professional cleaning and presentation improvement.", icon: Sparkles },
  { id: "card_rehab", title: "MAKE MY CARD LOOK ITS BEST", copy: "Let CardScience HQ evaluate the card and perform the appropriate services within this service level.", icon: Wand2 },
  { id: "let_us_decide", title: "I'M NOT SURE — LET CARDSCIENCE HQ DECIDE", copy: "Our team will screen the card and recommend the appropriate service. You approve anything extra before we ever charge it.", icon: HelpCircle, welcoming: true },
];
const INTENT_IDS = INTENTS.map((i) => i.id);

// Full legal points — consolidated under a single acknowledgment, viewable on demand.
const ACK_POINTS = [
  "Results, grades, monetary value increases and specific outcomes are not guaranteed, and not every card is gradeable.",
  "Some cards may be declined if CardScience HQ believes they should not be treated.",
  "I have described my card(s) accurately to the best of my knowledge.",
  "I will follow CardScience HQ’s shipping and packaging instructions; submissions sent outside the required protocol may be refused or returned.",
  "Orders declared $50+ follow documented packing procedures, and cards declared $500+ require applicable shipping insurance.",
  "Carriers and third-party insurers operate under their own terms and claim approval cannot be guaranteed.",
  "CardScience HQ is not affiliated with PSA or other grading companies, and my payment is for the selected service — not a guarantee of any grade or result.",
];

const STATUS_STEPS = ["submitted", "paid", "approved", "received", "in_lab", "ready", "returned"];
const STATUS_LABEL = { submitted: "Submitted", paid: "Paid", approved: "Approved", received: "Received", in_lab: "In The Lab", ready: "Ready", returned: "Returned", declined: "Declined" };
const STEP_LABELS = ["Your Card", "Service", "Send It In"];

const PERMISSION_OPTIONS = [
  { id: "no_private", label: "No — keep private", hint: "Default. Nothing is shown publicly." },
  { id: "website", label: "Yes — website", hint: "Permitted images may appear on this site." },
  { id: "social", label: "Yes — social media", hint: "Permitted images may appear on our socials." },
  { id: "website_social", label: "Yes — website & social", hint: "Permitted on both site and socials." },
];
const PERMISSION_LABEL = Object.fromEntries(PERMISSION_OPTIONS.map((o) => [o.id, o.label]));

const field = "w-full bg-[color:var(--charcoal)] border border-[color:var(--line)] rounded-xl px-4 py-3 text-white placeholder:text-[color:var(--chrome-3)] focus:outline-none focus:border-[color:var(--cyan)] transition-colors";

export default function SubmitCollectible() {
  const [searchParams] = useSearchParams();
  const preset = searchParams.get("service");

  const [phase, setPhase] = useState("form"); // form | payment | done
  const [step, setStep] = useState(1); // 1..3
  const [service, setService] = useState(INTENT_IDS.includes(preset) ? preset : "clean_polish");
  const [cardCount, setCardCount] = useState(1);
  const [card, setCard] = useState({ name: "", year: "", value: "" });
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [uploading, setUploading] = useState("");
  const [contact, setContact] = useState({ full_name: "", email: "", phone: "" });
  const [ship, setShip] = useState({ address1: "", address2: "", city: "", state: "", postal_code: "", country: "US" });
  const [marketingPermission, setMarketingPermission] = useState("no_private");
  const [agreed, setAgreed] = useState(false);
  const [showAckDetail, setShowAckDetail] = useState(false);

  const [order, setOrder] = useState(null);
  const [paid, setPaid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payCfg, setPayCfg] = useState(null);

  const [track, setTrack] = useState("");
  const [tracked, setTracked] = useState(null);
  const [showTrack, setShowTrack] = useState(false);
  const [recPaying, setRecPaying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/config`).then((r) => setPayCfg(r.data)).catch(() => setPayCfg({ configured: false }));
  }, []);

  const qty = Math.max(1, Number(cardCount) || 1);
  const intent = INTENTS.find((i) => i.id === service);

  // Server-computed quote is the single source of truth for every price shown.
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams({ service_type: service, card_count: String(qty), email: (contact.email || "").trim() });
    axios.get(`${API}/promo/first-card-quote?${params.toString()}`)
      .then((r) => setQuote(r.data)).catch(() => setQuote(null));
    setOrder(null);
  }, [service, qty, contact.email]);

  const estTotal = quote ? quote.base_cents / 100 : estimateTotal(service, qty);
  const dueTotal = quote ? quote.total_cents / 100 : estTotal;
  const discountAmt = (quote?.discount_cents || 0) / 100;
  const perCard = qty ? estTotal / qty : 0;
  const payAmountCents = quote ? quote.total_cents : Math.round(estTotal * 100);

  const upCard = (k, v) => { setCard((c) => ({ ...c, [k]: v })); setOrder(null); };
  const upContact = (k, v) => { setContact((c) => ({ ...c, [k]: v })); setOrder(null); };
  const upShip = (k, v) => { setShip((s) => ({ ...s, [k]: v })); setOrder(null); };

  const uploadSingle = async (file, setter, slot) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter({ path: data.path, url: `${BACKEND_URL}${data.url}`, filename: data.filename, content_type: data.content_type });
      setOrder(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload photo.");
    } finally { setUploading(""); }
  };

  const stepValid = () => {
    if (step === 1) return !!card.name.trim() && qty >= 1;
    if (step === 2) return !!service;
    if (step === 3) return contact.full_name && contact.email && ship.address1 && ship.city && ship.state && ship.postal_code && agreed;
    return false;
  };

  const next = () => {
    if (!stepValid()) { toast.error("Please complete this step to continue."); return; }
    setStep((s) => Math.min(3, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const proceedToPayment = async () => {
    if (!stepValid()) { toast.error("Please complete your details and accept the terms to continue."); return; }
    setLoading(true);
    try {
      let current = order;
      if (!current) {
        const desc = [card.name.trim(), card.year.trim()].filter(Boolean).join(" · ");
        const photos = [front, back].filter(Boolean).map((p) => ({ path: p.path, url: p.url, filename: p.filename, content_type: p.content_type }));
        const payload = {
          ...contact,
          collectible_category: "cards",
          collectible_type: card.name.trim() || null,
          declared_value: card.value ? Number(String(card.value).replace(/[^0-9.]/g, "")) || null : null,
          service_type: service,
          acknowledged: true,
          card_count: qty,
          price_per_card: perCard,
          estimated_total: estTotal,
          return_shipping: ship,
          notes: desc ? `Card: ${desc}` : null,
          marketing_permission: marketingPermission,
          cards: [{ description: desc || intent?.title || service, quantity: qty }],
          photos,
        };
        const { data } = await axios.post(`${API}/service-orders`, payload);
        current = data;
        setOrder(data);
      }
      if (payCfg?.configured && dueTotal > 0) {
        setPhase("payment");
      } else {
        setPaid(null);
        setPhase("done");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const doTrack = async () => {
    if (!track.trim()) return;
    try {
      const { data } = await axios.get(`${API}/service-orders/${track.trim()}`);
      setTracked(data); setRecPaying(false);
    } catch { setTracked(null); toast.error("No submission found with that reference."); }
  };

  const refreshTracked = async () => {
    try { const { data } = await axios.get(`${API}/service-orders/${tracked.reference}`); setTracked(data); } catch {}
  };
  const declineRec = async () => {
    try {
      await axios.post(`${API}/service-orders/${tracked.reference}/recommendation/decline`);
      toast.success("Recommendation declined. We won't perform the extra work.");
      setRecPaying(false); refreshTracked();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update. Try again."); }
  };
  const approveRec = async () => {
    const rec = tracked.recommendation || {};
    if ((rec.additional_cost_cents || 0) > 0) { setRecPaying(true); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    try {
      await axios.post(`${API}/service-orders/${tracked.reference}/recommendation/approve`);
      toast.success("Approved — thank you!");
      refreshTracked();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve. Try again."); }
  };

  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  /* ---------------- PAYMENT PHASE ---------------- */
  if (phase === "payment" && order) {
    return (
      <div className="bg-ink min-h-screen pb-24">
        <PageHead eyebrow="Secure Payment" title="COMPLETE YOUR PAYMENT." tone="cyan"
          sub="Your submission is reserved. Pay securely to confirm it — we’ll then email your submission number and next steps." />
        <Wrap className="py-14 max-w-[560px]">
          <SquarePayment
            reference={order.reference}
            amountCents={payAmountCents}
            onSuccess={(pdata) => { setPaid(pdata); setPhase("done"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          />
          <button data-testid="pay-back" onClick={() => { setPhase("form"); setStep(3); }}
            className="mx-auto mt-6 flex items-center gap-2 text-sm text-[color:var(--chrome-3)] hover:text-white transition-colors">
            <ArrowLeft size={15} /> Back (your details are saved)
          </button>
        </Wrap>
      </div>
    );
  }

  /* ---------------- DONE / CONFIRMATION ---------------- */
  if (phase === "done" && order) {
    return (
      <div className="bg-ink min-h-screen pb-24">
        <PageHead eyebrow={paid ? "Payment Received" : "Submission Received"}
          title={paid ? "YOUR SUBMISSION IS CONFIRMED." : "YOUR SUBMISSION IS IN REVIEW."} tone="cyan" />
        <Wrap className="py-14 max-w-[720px]">
          <div className="rounded-3xl holo-border holo-glow p-8 text-center">
            <CheckCircle2 size={40} className="text-holo mx-auto mb-4" />
            {paid && (
              <p data-testid="paid-badge" className="inline-flex items-center gap-2 rounded-full border border-[color:var(--cyan)]/50 px-4 py-1.5 text-xs font-tech tracking-widest text-[color:var(--cyan)] mb-4">
                <ShieldCheck size={14} /> PAID · {money((paid.amount_cents ?? payAmountCents) / 100)}
              </p>
            )}
            <p className="text-sm text-[color:var(--chrome-2)]">Your CardScience HQ submission number</p>
            <p data-testid="submit-reference" className="font-display font-black text-3xl text-holo mt-1 tracking-wide">{order.reference}</p>
            <p className="text-sm text-[color:var(--chrome-3)] mt-3 max-w-lg mx-auto">
              A confirmation has been emailed to <span className="text-[color:var(--chrome-1)]">{contact.email}</span>. Save your submission number to track progress.
            </p>
          </div>

          <div className="mt-8 rounded-3xl border border-[color:var(--line)] p-7">
            <p className="eyebrow mb-4 flex items-center gap-2"><ClipboardCheck size={15} className="text-[color:var(--cyan)]" /> What to do next</p>
            <ol className="space-y-4">
              {[
                ["We review your submission", "Within 1 business day we’ll email your secure shipping address and a packing checklist. Please wait for that email before mailing anything so we can log your inbound package."],
                ["Protect your card", "Penny sleeve the card, then place it in a top-loader or semi-rigid holder. Do not tape the card or holder directly."],
                ["Pack it safely", "Sandwich the protected card between two pieces of cardboard, secure with a rubber band, and place inside a bubble mailer or small box. Include a slip with your submission number " + order.reference + "."],
                ["Ship to the address we send you", "Use the exact address and carrier in your shipping email. Submissions declared $50+ follow our documented packing procedures; $500+ require applicable shipping insurance."],
              ].map(([t, d], i) => (
                <li key={i} className="flex gap-4">
                  <span className="h-7 w-7 shrink-0 rounded-full holo-border flex items-center justify-center text-sm font-display font-black text-holo">{i + 1}</span>
                  <div>
                    <p className="text-white font-semibold text-sm">{t}</p>
                    <p className="text-sm text-[color:var(--chrome-2)] mt-1 leading-relaxed">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-6 rounded-2xl border border-[color:var(--magenta)]/40 p-6 flex items-start gap-3">
            <Ban size={20} className="text-[color:var(--magenta)] shrink-0 mt-0.5" />
            <p className="text-sm text-[color:var(--chrome-2)]">
              <span className="text-white font-semibold">Do not ship yet.</span> Wait for your shipping email so we can document your package on arrival. No grade or outcome is guaranteed.
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/shipping-insurance" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">READ SHIPPING & INSURANCE <ArrowRight size={16} /></Link>
            <button onClick={() => window.location.reload()} className="btn-ghost rounded-xl px-7 py-4 text-sm">START ANOTHER SUBMISSION</button>
          </div>
        </Wrap>
      </div>
    );
  }

  /* ---------------- FORM WIZARD (3 steps) ---------------- */
  return (
    <div className="bg-ink min-h-screen pb-24">
      <PageHead eyebrow="Submit Your Card" title="START YOUR SUBMISSION." tone="violet"
        sub="Three quick steps. You don’t have to diagnose your card — that’s our job." />

      <Wrap className="py-12 max-w-[680px]">
        {/* progress */}
        <div className="flex items-center justify-between mb-10">
          {STEP_LABELS.map((label, i) => {
            const n = i + 1;
            const done = n < step, active = n === step;
            return (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center gap-1.5" data-testid={`wizard-step-${n}`}>
                  <span className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-display font-black transition-colors ${active ? "holo-border text-holo" : done ? "bg-[color:var(--cyan)] text-black" : "border border-[color:var(--line)] text-[color:var(--chrome-3)]"}`}>
                    {done ? <CheckCircle2 size={15} /> : n}
                  </span>
                  <span className={`text-[10px] font-tech tracking-wide ${active ? "text-white" : "text-[color:var(--chrome-3)]"}`}>{label}</span>
                </div>
                {n < STEP_LABELS.length && <span className={`flex-1 h-px mx-1 ${done ? "bg-[color:var(--cyan)]" : "bg-[color:var(--line)]"}`} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* STEP 1 — YOUR CARD */}
        {step === 1 && (
          <div>
            <h2 className="h-display text-2xl text-chrome mb-1">1. Your card</h2>
            <p className="text-sm text-[color:var(--chrome-3)] mb-6">Just the basics. Photos are optional — we’ll inspect everything when it arrives.</p>

            <div className="space-y-3">
              <input data-testid="card-name" value={card.name} onChange={(e) => upCard("name", e.target.value)} placeholder="Card / player name" className={field} />
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="card-year" value={card.year} onChange={(e) => upCard("year", e.target.value)} placeholder="Year or set (optional)" className={field} />
                <input data-testid="card-value" value={card.value} onChange={(e) => upCard("value", e.target.value)} placeholder="Estimated value (optional)" className={field} />
              </div>
            </div>

            {/* optional front/back photos */}
            <p className="eyebrow mt-7 mb-2">Photos (optional)</p>
            <div className="flex flex-wrap gap-3">
              {[["Front", front, setFront, "front"], ["Back", back, setBack, "back"]].map(([lbl, val, setter, slot]) => (
                <div key={slot}>
                  {val ? (
                    <div data-testid={`photo-${slot}`} className="relative h-28 w-28 rounded-xl overflow-hidden border border-[color:var(--line)] group">
                      <img src={val.url} alt={lbl} className="h-full w-full object-cover" />
                      <button type="button" data-testid={`remove-${slot}`} onClick={() => { setter(null); setOrder(null); }}
                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={13} /></button>
                      <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] font-tech tracking-widest px-1.5 py-0.5 rounded">{lbl.toUpperCase()}</span>
                    </div>
                  ) : (
                    <label data-testid={`upload-${slot}`}
                      className="h-28 w-28 rounded-xl border border-dashed border-[color:var(--line-2)] flex flex-col items-center justify-center gap-1.5 cursor-pointer text-[color:var(--chrome-3)] hover:border-[color:var(--cyan)] hover:text-[color:var(--cyan)] transition-colors">
                      {uploading === slot ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} />}
                      <span className="text-[10px] font-tech tracking-wide">{uploading === slot ? "UPLOADING" : `${lbl.toUpperCase()} PHOTO`}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadSingle(e.target.files?.[0], setter, slot); e.target.value = ""; }} />
                    </label>
                  )}
                </div>
              ))}
            </div>

            {/* quantity */}
            <p className="eyebrow mt-7 mb-2">How many cards?</p>
            <div className="flex items-center gap-4">
              <button type="button" data-testid="qty-minus" onClick={() => { setCardCount((c) => Math.max(1, (Number(c) || 1) - 1)); setOrder(null); }}
                className="h-12 w-12 rounded-xl border border-[color:var(--line)] flex items-center justify-center text-white hover:border-[color:var(--cyan)]"><Minus size={18} /></button>
              <input data-testid="qty-input" type="number" min="1" value={cardCount}
                onChange={(e) => { setCardCount(e.target.value.replace(/[^0-9]/g, "")); setOrder(null); }}
                onBlur={() => setCardCount(qty)}
                className={`${field} max-w-[120px] text-center text-xl font-display font-black`} />
              <button type="button" data-testid="qty-plus" onClick={() => { setCardCount((c) => (Number(c) || 1) + 1); setOrder(null); }}
                className="h-12 w-12 rounded-xl border border-[color:var(--line)] flex items-center justify-center text-white hover:border-[color:var(--cyan)]"><Plus size={18} /></button>
              <span className="text-sm text-[color:var(--chrome-2)]">card{qty > 1 ? "s" : ""}</span>
            </div>
          </div>
        )}

        {/* STEP 2 — WHAT WOULD YOU LIKE US TO DO? */}
        {step === 2 && (
          <div>
            <h2 className="h-display text-2xl text-chrome mb-1">2. What would you like us to do?</h2>
            <p className="text-sm text-[color:var(--chrome-3)] mb-6">Pick the outcome you want. We handle the rest.</p>

            <div className="space-y-3">
              {INTENTS.map((o) => {
                const selected = service === o.id;
                const Icon = o.icon;
                return (
                  <button type="button" key={o.id} data-testid={`intent-${o.id}`} onClick={() => { setService(o.id); setOrder(null); }}
                    className={`w-full rounded-2xl border px-5 py-5 text-left transition-all flex items-start gap-4 ${selected ? "holo-border holo-glow text-white" : o.welcoming ? "border-[color:var(--cyan)]/40 bg-[color:var(--cyan)]/5 text-[color:var(--chrome-1)] hover:border-[color:var(--cyan)]" : "border-[color:var(--line)] text-[color:var(--chrome-2)] hover:border-[color:var(--chrome-3)]"}`}>
                    <span className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center ${selected || o.welcoming ? "text-[color:var(--cyan)]" : "text-[color:var(--chrome-3)]"} border border-[color:var(--line-2)]`}>
                      <Icon size={20} />
                    </span>
                    <span className="min-w-0">
                      <span className={`block font-display font-black text-base sm:text-lg ${selected ? "text-holo" : "text-white"}`}>{o.title}</span>
                      <span className="block text-sm text-[color:var(--chrome-2)] mt-1 leading-relaxed">{o.copy}</span>
                    </span>
                    {selected && <CheckCircle2 size={20} className="text-[color:var(--cyan)] shrink-0 ml-auto" />}
                  </button>
                );
              })}
            </div>

            <p data-testid="not-sure-reassurance" className="text-sm text-[color:var(--cyan)] mt-5 text-center">
              Don’t know what your card needs? Perfect. That’s why you’re sending it to us.
            </p>

            {/* live estimate */}
            <div className="mt-7 rounded-2xl holo-border p-5 flex items-center justify-between">
              <div>
                <p className="eyebrow mb-1">Estimated total</p>
                <p className="text-xs text-[color:var(--chrome-3)]">{qty} × {money(perCard)}/card{service === "let_us_decide" ? " · base rate, extras only if you approve" : ""}</p>
              </div>
              <p data-testid="running-total" className="h-display text-3xl text-holo">{money(estTotal)}</p>
            </div>
            {service === "clean_polish" && qty < 3 && (
              <div data-testid="promo-unlock-hint" className="mt-3 rounded-xl border border-[color:var(--cyan)]/40 bg-[color:var(--cyan)]/5 px-4 py-3">
                <p className="text-sm text-[color:var(--cyan)] font-semibold">Add 3 cards to unlock your FREE first cleaning.</p>
              </div>
            )}
            {service === "clean_polish" && qty >= 3 && (quote?.eligible ?? true) && (
              <div data-testid="promo-active-hint" className="mt-3 rounded-xl border border-[color:var(--cyan)]/40 bg-[color:var(--cyan)]/5 px-4 py-3">
                <p className="text-sm text-[color:var(--cyan)] font-semibold">Your first card is on the house on this order.</p>
              </div>
            )}
          </div>
        )}

        {/* STEP 3 — SEND IT IN */}
        {step === 3 && (
          <div>
            <h2 className="h-display text-2xl text-chrome mb-1">3. Send it in</h2>
            <p className="text-sm text-[color:var(--chrome-3)] mb-6">Where should we return your card, and how would you like to pay?</p>

            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="sub-name" value={contact.full_name} onChange={(e) => upContact("full_name", e.target.value)} placeholder="Full name" className={field} />
              <input data-testid="sub-email" type="email" value={contact.email} onChange={(e) => upContact("email", e.target.value)} placeholder="Email" className={field} />
              <input data-testid="sub-phone" value={contact.phone} onChange={(e) => upContact("phone", e.target.value)} placeholder="Phone (optional)" className={field} />
              <div />
              <input data-testid="ship-address1" value={ship.address1} onChange={(e) => upShip("address1", e.target.value)} placeholder="Return address line 1" className={`${field} sm:col-span-2`} />
              <input data-testid="ship-address2" value={ship.address2} onChange={(e) => upShip("address2", e.target.value)} placeholder="Apartment, suite (optional)" className={`${field} sm:col-span-2`} />
              <input data-testid="ship-city" value={ship.city} onChange={(e) => upShip("city", e.target.value)} placeholder="City" className={field} />
              <input data-testid="ship-state" value={ship.state} onChange={(e) => upShip("state", e.target.value)} placeholder="State / Province" className={field} />
              <input data-testid="ship-zip" value={ship.postal_code} onChange={(e) => upShip("postal_code", e.target.value)} placeholder="ZIP / Postal code" className={field} />
              <input data-testid="ship-country" value={ship.country} onChange={(e) => upShip("country", e.target.value)} placeholder="Country" className={field} />
            </div>

            {/* order summary */}
            <div data-testid="order-summary" className="mt-6 rounded-3xl holo-border p-6 space-y-2">
              <Row label="Card" value={[card.name, card.year].filter(Boolean).join(" · ")} />
              <Row label="Service" value={intent?.title} />
              <div className="flex items-center justify-between text-sm pt-1">
                <span className="text-[color:var(--chrome-2)]">Service subtotal ({qty} × {money(perCard)})</span>
                <span data-testid="service-subtotal" className="text-[color:var(--chrome-1)]">{money(estTotal)}</span>
              </div>
              {quote?.eligible && discountAmt > 0 && (
                <div data-testid="promo-discount-row" className="flex items-center justify-between text-sm">
                  <span className="text-[color:var(--cyan)] font-semibold">{quote.promo_label}</span>
                  <span className="text-[color:var(--cyan)] font-semibold">−{money(discountAmt)}</span>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-[color:var(--line)] pt-3 mt-1">
                <span className="eyebrow">Amount due</span>
                <span data-testid="amount-due" className="h-display text-3xl text-holo">{money(dueTotal)}</span>
              </div>
            </div>

            {/* privacy preference */}
            <div className="mt-7" data-testid="marketing-permission">
              <p className="eyebrow mb-2 flex items-center gap-2"><ShieldCheck size={13} className="text-[color:var(--cyan)]" /> Privacy preference</p>
              <p className="text-sm text-[color:var(--chrome-3)] mb-4">
                Your submission is <span className="text-[color:var(--chrome-1)]">private by default</span>. We never show your card publicly unless you allow it.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {PERMISSION_OPTIONS.map((o) => (
                  <button type="button" key={o.id} data-testid={`permission-${o.id}`}
                    onClick={() => { setMarketingPermission(o.id); setOrder(null); }}
                    className={`rounded-xl border px-4 py-3.5 text-left transition-all ${marketingPermission === o.id ? "holo-border text-white" : "border-[color:var(--line)] text-[color:var(--chrome-2)] hover:border-[color:var(--chrome-3)]"}`}>
                    <div className="flex items-center gap-2">
                      {o.id === "no_private" && <Lock size={13} className="text-[color:var(--cyan)]" />}
                      <span className="text-sm font-semibold">{o.label}</span>
                      {o.id === "no_private" && <span className="ml-auto text-[9px] font-tech tracking-widest text-[color:var(--cyan)]">DEFAULT</span>}
                    </div>
                    <p className="text-[11px] text-[color:var(--chrome-3)] mt-1">{o.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* consolidated acknowledgment */}
            <div className="mt-7 rounded-2xl border border-[color:var(--line)] p-5">
              <label data-testid="consolidated-ack" className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={agreed} onChange={() => setAgreed((v) => !v)} className="mt-1 accent-[color:var(--cyan)] h-4 w-4 shrink-0" />
                <span className="text-xs text-[color:var(--chrome-1)] leading-relaxed">
                  I understand and agree to CardScience HQ’s service terms: results and grades are not guaranteed, some cards may be declined, I’ve described my card accurately, and I’ll follow the shipping &amp; packaging instructions. My payment is for the selected service — not a guaranteed outcome. See our <Link to="/terms" className="text-[color:var(--cyan)] underline">Terms</Link>.
                </span>
              </label>
              <button type="button" data-testid="ack-detail-toggle" onClick={() => setShowAckDetail((v) => !v)}
                className="mt-3 flex items-center gap-1.5 text-[11px] font-tech tracking-wide text-[color:var(--chrome-3)] hover:text-white transition-colors">
                <ChevronDown size={13} className={`transition-transform ${showAckDetail ? "rotate-180" : ""}`} /> {showAckDetail ? "HIDE FULL DETAILS" : "VIEW FULL ACKNOWLEDGMENT DETAILS"}
              </button>
              {showAckDetail && (
                <ul className="mt-3 space-y-2 border-t border-[color:var(--line)] pt-3">
                  {ACK_POINTS.map((t, i) => (
                    <li key={i} className="flex gap-2 text-xs text-[color:var(--chrome-2)] leading-relaxed">
                      <ShieldAlert size={13} className="text-[color:var(--magenta)] shrink-0 mt-0.5" /> {t}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* nav */}
        <div className="mt-8 flex items-center gap-3">
          {step > 1 && (
            <button type="button" data-testid="wizard-back" onClick={goBack} className="btn-ghost rounded-xl px-6 py-4 text-sm inline-flex items-center gap-2">
              <ArrowLeft size={16} /> Back
            </button>
          )}
          {step < 3 ? (
            <button type="button" data-testid="wizard-next" onClick={next} disabled={!stepValid()}
              className="btn-holo flex-1 rounded-xl py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button type="button" data-testid="proceed-payment" onClick={proceedToPayment} disabled={!stepValid() || loading}
              className="btn-holo flex-1 rounded-xl py-4 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              {loading ? <><Loader2 size={16} className="animate-spin" /> PREPARING…</>
                : <><Truck size={16} /> {payCfg?.configured ? `PROCEED TO SECURE PAYMENT · ${money(dueTotal)}` : "SUBMIT SUBMISSION"}</>}
            </button>
          )}
        </div>

        {/* Track a submission */}
        <div className="mt-12 border-t border-[color:var(--line)] pt-6">
          <button data-testid="track-toggle" onClick={() => setShowTrack((v) => !v)}
            className="flex items-center gap-2 text-sm text-[color:var(--chrome-3)] hover:text-white transition-colors">
            <Search size={15} /> Track an existing submission
          </button>
          {showTrack && (
            <div className="mt-4">
              <div className="flex gap-2 max-w-sm">
                <input data-testid="track-input" value={track} onChange={(e) => setTrack(e.target.value)} placeholder="LAB-XXXXXX" className={field} />
                <button data-testid="track-btn" onClick={doTrack} className="btn-holo rounded-xl px-4 shrink-0"><ArrowRight size={16} /></button>
              </div>
              {tracked && (
                <div data-testid="track-result" className="mt-5">
                  <p className="text-sm text-[color:var(--chrome-2)]">{tracked.reference}</p>

                  {/* ACTION REQUIRED — recommendation */}
                  {tracked.recommendation && recPaying && tracked.recommendation.status === "pending" ? (
                    <div className="mt-4">
                      <p className="eyebrow mb-3 flex items-center gap-2"><ShieldAlert size={13} className="text-[color:var(--cyan)]" /> Approve recommended work</p>
                      <SquarePayment
                        reference={tracked.reference}
                        amountCents={tracked.recommendation.additional_cost_cents}
                        payEndpoint="/payments/recommendation"
                        onSuccess={() => { toast.success("Approved & paid — thank you!"); setRecPaying(false); refreshTracked(); }}
                      />
                      <button data-testid="rec-pay-back" onClick={() => setRecPaying(false)}
                        className="mx-auto mt-4 flex items-center gap-2 text-sm text-[color:var(--chrome-3)] hover:text-white transition-colors">
                        <ArrowLeft size={15} /> Back
                      </button>
                    </div>
                  ) : tracked.recommendation && tracked.recommendation.status === "pending" ? (
                    <div data-testid="action-required" className="mt-4 rounded-2xl holo-border holo-glow p-6">
                      <p className="inline-flex items-center gap-2 text-[11px] font-tech tracking-widest text-[color:var(--cyan)] mb-2">
                        <ShieldAlert size={14} /> ACTION REQUIRED
                      </p>
                      <p className="h-display text-2xl text-holo">WE FOUND SOMETHING.</p>
                      <div className="mt-4 space-y-3">
                        <div>
                          <p className="text-[11px] font-tech tracking-widest text-[color:var(--chrome-3)] uppercase">Recommended service</p>
                          <p data-testid="rec-service" className="text-sm text-[color:var(--chrome-1)] mt-0.5">{tracked.recommendation.service}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-tech tracking-widest text-[color:var(--chrome-3)] uppercase">Why we recommend it</p>
                          <p data-testid="rec-reason" className="text-sm text-[color:var(--chrome-1)] mt-0.5 leading-relaxed">{tracked.recommendation.reason}</p>
                        </div>
                        <div>
                          <p className="text-[11px] font-tech tracking-widest text-[color:var(--chrome-3)] uppercase">Additional cost</p>
                          <p data-testid="rec-cost" className="text-sm font-semibold text-[color:var(--cyan)] mt-0.5">
                            {tracked.recommendation.additional_cost_cents ? money(tracked.recommendation.additional_cost_cents / 100) : "No additional charge"}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-[color:var(--chrome-3)] mt-4">You are never charged for extra work unless you approve.</p>
                      <div className="mt-4 flex gap-3">
                        <button data-testid="rec-approve" onClick={approveRec}
                          className="btn-holo flex-1 rounded-xl py-3.5 text-sm inline-flex items-center justify-center gap-2">
                          <CheckCircle2 size={16} /> APPROVE
                        </button>
                        <button data-testid="rec-decline" onClick={declineRec}
                          className="btn-ghost flex-1 rounded-xl py-3.5 text-sm inline-flex items-center justify-center gap-2">
                          <Ban size={16} /> NO THANKS
                        </button>
                      </div>
                    </div>
                  ) : tracked.recommendation && tracked.recommendation.status === "approved" ? (
                    <div data-testid="rec-approved" className="mt-4 rounded-2xl border border-[color:var(--cyan)]/40 bg-[color:var(--cyan)]/5 p-4">
                      <p className="text-sm font-semibold text-[color:var(--cyan)] flex items-center gap-2"><CheckCircle2 size={15} /> Recommendation approved — thank you!</p>
                      <p className="text-xs text-[color:var(--chrome-2)] mt-1">{tracked.recommendation.service}</p>
                    </div>
                  ) : tracked.recommendation && tracked.recommendation.status === "declined" ? (
                    <div data-testid="rec-declined" className="mt-4 rounded-2xl border border-[color:var(--line)] p-4">
                      <p className="text-sm text-[color:var(--chrome-2)]">You declined the recommended extra work. We'll proceed with your original service.</p>
                    </div>
                  ) : null}

                  {tracked.status === "declined" ? (
                    <div className="mt-3 rounded-xl border border-[color:var(--magenta)]/40 bg-[color:var(--magenta)]/5 p-3">
                      <p className="text-sm font-semibold text-[color:var(--magenta)]">Declined</p>
                      {tracked.decline_reason && <p className="text-xs text-[color:var(--chrome-1)] mt-1.5 leading-relaxed">{tracked.decline_reason}</p>}
                    </div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      {STATUS_STEPS.map((s) => {
                        const active = STATUS_STEPS.indexOf(s) <= STATUS_STEPS.indexOf(tracked.status);
                        return (
                          <div key={s} className="flex items-center gap-3">
                            <span className={`h-2.5 w-2.5 rounded-full ${active ? "bg-[color:var(--cyan)]" : "bg-[color:var(--line)]"}`} />
                            <span className={`text-sm ${active ? "text-white" : "text-[color:var(--chrome-3)]"}`}>{STATUS_LABEL[s]}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {tracked.shipping_instructions && (
                    <div className="mt-4 rounded-xl border border-[color:var(--cyan)]/40 bg-[color:var(--cyan)]/5 p-3">
                      <p className="eyebrow mb-1.5 flex items-center gap-1.5"><PackageCheck size={13} className="text-[color:var(--cyan)]" /> Approved — Shipping Instructions</p>
                      <pre className="whitespace-pre-wrap text-xs text-[color:var(--chrome-1)] font-sans leading-relaxed">{tracked.shipping_instructions}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Wrap>
    </div>
  );
}

const Row = ({ label, value, onEdit }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0">
      <p className="text-[11px] font-tech tracking-widest text-[color:var(--chrome-3)] uppercase">{label}</p>
      <p className="text-sm text-[color:var(--chrome-1)] mt-0.5 break-words">{value || "—"}</p>
    </div>
    {onEdit && <button onClick={onEdit} className="text-xs text-[color:var(--cyan)] hover:opacity-80 shrink-0">Edit</button>}
  </div>
);
